import React, { useState, useEffect } from "react";
// Import Convex reactive query hook
import { useQuery } from "convex/react";
// Import database API routes definition
import { api } from "../../convex/_generated/api";
// Import our child billing page components
import { SubscriptionStatus } from "../components/billing/SubscriptionStatus";
import { PricingTable } from "../components/billing/PricingTable";
import { PaymentForm } from "../components/billing/PaymentForm";
import { MobilePayQR } from "../components/billing/MobilePayQR";

/**
 * BillingPage Component
 * Serves as the billing control center for MultiKeys.
 * Composes pricing charts, checkout elements, status blocks, and desktop-to-mobile handoffs.
 */
export const BillingPage: React.FC = () => {
  // Query user plan state reactively from the backend database (reactive syncing G4)
  const planInfo = useQuery(api.users.getUserPlan);

  // Component state variables
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string>("");
  const [showSuccessBanner, setShowSuccessBanner] = useState<boolean>(false);
  const [previousPlan, setPreviousPlan] = useState<string | null>(null);

  // Synchronize plan changes reactively and dismiss checkout elements upon successful upgrades
  useEffect(() => {
    if (!planInfo) return;

    if (previousPlan === null) {
      // Initialize starting plan state
      setPreviousPlan(planInfo.plan);
    } else if (previousPlan !== planInfo.plan) {
      // Plan transitioned from a lower tier to a higher tier
      const upgradedToPaid =
        (previousPlan === "free" || previousPlan === "trial") &&
        (planInfo.plan === "pro" || planInfo.plan === "team");

      if (upgradedToPaid) {
        // Automatically dismiss active payment form checkout card
        setSelectedPriceId(null);
        setSubscriptionId("");
        // Show success/completion badge
        setShowSuccessBanner(true);
      }
      setPreviousPlan(planInfo.plan);
    }
  }, [planInfo, previousPlan]);

  // Loading state while Convex fetches user plan details
  if (planInfo === undefined) {
    return (
      <div
        className="billing-container"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "70vh",
        }}
        data-testid="billing-loading"
      >
        <div className="loading-spinner" style={{ borderTopColor: "#00D4FF", width: "48px", height: "48px", border: "4px solid rgba(255,255,255,0.1)", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
        <p style={{ marginTop: "1rem", color: "var(--color-text-secondary)" }}>
          Loading billing settings...
        </p>
      </div>
    );
  }

  // Fallback if user profile doesn't exist
  if (planInfo === null) {
    return (
      <div className="billing-container" data-testid="billing-empty">
        <div style={{ textAlign: "center", padding: "4rem 2rem" }}>
          <h2>No Profile Found</h2>
          <p>Please authenticate or log in to view your billing dashboard.</p>
        </div>
      </div>
    );
  }

  const { plan } = planInfo;

  return (
    <div className="billing-container" data-testid="billing-page-container">
      {/* Reactive upgrade success notification banner */}
      {showSuccessBanner && (
        <div
          className="billing-success-banner"
          style={{
            backgroundColor: "rgba(0, 212, 255, 0.1)",
            border: "1px solid #00D4FF",
            borderRadius: "var(--border-radius-md)",
            padding: "1.5rem",
            textAlign: "center",
            boxShadow: "0 0 15px rgba(0, 212, 255, 0.2)",
          }}
          data-testid="billing-success-banner"
        >
          <span style={{ fontSize: "2rem", color: "#00D4FF" }} role="img" aria-label="Success Check">✓</span>
          <h3 style={{ color: "#FFFFFF", fontSize: "1.2rem", fontWeight: "700", marginTop: "0.5rem", marginBottom: "0.5rem" }}>
            Upgrade Completed!
          </h3>
          <p style={{ color: "var(--color-text-secondary)", fontSize: "0.9rem", maxWidth: "500px", margin: "0 auto" }}>
            Your subscription has been successfully provisioned. Your desktop client keys are now unlocked.
          </p>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowSuccessBanner(false)}
            style={{ marginTop: "1rem", padding: "0.35rem 1rem", fontSize: "0.8rem" }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Top Section: User's Active Plan & Billing Management Details */}
      <section className="billing-status-section" data-testid="billing-status-section">
        <SubscriptionStatus />
      </section>

      {/* Checkout Area: Displays Checkout Elements Form + QR Handshake Panel */}
      {selectedPriceId && (
        <section
          className="checkout-workspace-section"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "2rem",
            alignItems: "start",
            backgroundColor: "var(--color-bg-secondary)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--border-radius-lg)",
            padding: "2rem",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.25)",
          }}
          data-testid="checkout-workspace-section"
        >
          <div className="payment-form-card" data-testid="payment-form-card">
            <PaymentForm
              priceId={selectedPriceId}
              onCancel={() => {
                setSelectedPriceId(null);
                setSubscriptionId("");
              }}
              onSubscriptionCreated={(subId) => setSubscriptionId(subId)}
              onSuccess={() => {
                // Succesful payment updates banner
                setShowSuccessBanner(true);
                setSelectedPriceId(null);
                setSubscriptionId("");
              }}
            />
          </div>

          {/* Desktop to Mobile Pay QR Handoff panel (hidden responsive on media screen width < 768px) */}
          {subscriptionId && (
            <div className="qr-panel-wrapper" data-testid="qr-panel-wrapper">
              <MobilePayQR subscriptionId={subscriptionId} />
            </div>
          )}
        </section>
      )}

      {/* Tiers Section: Display comparative charts when user is not on active paid accounts */}
      {!selectedPriceId && (plan === "free" || plan === "trial") && (
        <section className="pricing-table-section" data-testid="pricing-table-section">
          <PricingTable
            onSelectPrice={(priceId) => {
              setSelectedPriceId(priceId);
              setSubscriptionId("");
            }}
          />
        </section>
      )}
    </div>
  );
};
